
import { Box, Chip, Stack, Typography } from "@mui/material";

import type { WidgetProps } from "./types";

// Renders a single planned action — Pipedrive's `next_activity` is the natural
// upstream. Widget is generic enough for any deal-room page; on the proposal
// page it sits below SM-authored terms and shows the supplier what we're doing
// next.
export function NextStep({ widget }: WidgetProps<"next_step">) {
    const { title, subject, kind, dueDate, note, ownerName } = widget.props;

    return (
        <Box
            sx={{
                p: 3,
                border: 1,
                borderColor: "divider",
                borderRadius: "10px",
                bgcolor: "background.paper",
                display: "flex",
                gap: 2.5,
                alignItems: "flex-start",
            }}
        >
            <KindBadge kind={kind ?? "other"} />
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: "0.08em" }}>
                        {title ?? "Up next"}
                    </Typography>
                    {dueDate && (
                        <Chip
                            size="small"
                            label={formatDueDate(dueDate)}
                            sx={{ fontWeight: 600 }}
                        />
                    )}
                </Stack>
                <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                    {subject}
                </Typography>
                {note && (
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                        {note}
                    </Typography>
                )}
                {ownerName && (
                    <Typography variant="caption" color="text.secondary">
                        Owned by {ownerName}
                    </Typography>
                )}
            </Stack>
        </Box>
    );
}

type Kind = NonNullable<WidgetProps<"next_step">["widget"]["props"]["kind"]>;

const kindMeta: Record<Kind, { label: string; icon: string }> = {
    call:     { label: "Call",     icon: "☎" },           // ☎
    meeting:  { label: "Meeting",  icon: "👥" },     // 👥
    email:    { label: "Email",    icon: "✉" },           // ✉
    task:     { label: "Task",     icon: "✓" },           // ✓
    deadline: { label: "Deadline", icon: "⏱" },           // ⏱
    lunch:    { label: "Lunch",    icon: "🍽" },     // 🍽
    other:    { label: "Action",   icon: "→" },           // →
};

function KindBadge({ kind }: { kind: Kind }) {
    const meta = kindMeta[kind];
    return (
        <Box
            sx={{
                width: 48,
                height: 48,
                borderRadius: "50%",
                bgcolor: "secondary.main",
                color: "common.white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontSize: 22,
                lineHeight: 1,
            }}
            aria-label={meta.label}
        >
            <span aria-hidden>{meta.icon}</span>
        </Box>
    );
}

function formatDueDate(iso: string): string {
    // Accept YYYY-MM-DD or full ISO. Display as "Mon, May 4" (locale-agnostic).
    const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        timeZone: "UTC",
    });
}
